// ═══════════════════════════════════════════════════════════
// GlobalSearchModal — Command Palette (Ctrl+K)
// Searches customers, appointments, staff + quick actions
// ═══════════════════════════════════════════════════════════
import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useT } from '../contexts/I18nContext';
import { useCustomerSearch } from '../hooks/useCustomerSearch';

const MONTH_SHORT = ['січ','лют','бер','квіт','трав','черв','лип','серп','вер','жовт','лист','груд'];

function fmtDate(dateStr) {
  if (!dateStr) return '';
  const [, m, d] = dateStr.split('-');
  return `${parseInt(d)} ${MONTH_SHORT[parseInt(m) - 1]}`;
}

function getInitials(name) {
  if (!name) return '?';
  return name.trim().split(/\s+/).map(n => n[0]).join('').toUpperCase().slice(0, 2);
}

function highlight(text, query) {
  if (!query || !text) return text || '';
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="gsearch-mark">{text.slice(idx, idx + query.length)}</mark>
      {text.slice(idx + query.length)}
    </>
  );
}

export default function GlobalSearchModal({ appointments, staff, onOpenCustomer, onNavigate, onOpenCalendar, onClose }) {
  const { t } = useT();
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  // Customer search runs server-side — no tenant-wide array needed.
  const { results: customerMatches } = useCustomerSearch(query, { limit: 6 });

  const quickActions = useMemo(() => [
    { id: 'qa-calendar',  icon: '📅', label: t('gsearch.goCalendar',  'Розклад — сьогодні'), action: () => { onOpenCalendar(new Date()); onNavigate('calendar'); } },
    { id: 'qa-clients',   icon: '👥', label: t('gsearch.goClients',   'Клієнтська база'),    action: () => onNavigate('clients') },
    { id: 'qa-finances',  icon: '💰', label: t('gsearch.goFinances',  'Фінанси'),            action: () => onNavigate('finances') },
    { id: 'qa-dashboard', icon: '📊', label: t('gsearch.goDashboard', 'Дешборд'),            action: () => onNavigate('dashboard') },
    { id: 'qa-new-client',icon: '➕', label: t('gsearch.newClient',   'Новий клієнт'),       action: () => { onOpenCustomer(null); } },
  ], [t, onNavigate, onOpenCalendar, onOpenCustomer]);

  // Build flat result list
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return quickActions;

    const items = [];

    // Customers — already fetched via useCustomerSearch above.
    const custMatches = customerMatches.map(c => ({
      id: 'c-' + c.id,
      type: 'customer',
      icon: null,
      initials: getInitials(c.name),
      color: c.color || '#6366f1',
      primary: c.name || c.phone,
      secondary: c.phone,
      action: () => onOpenCustomer(c),
    }));

    // Staff
    const staffMatches = (staff || [])
      .filter(s =>
        s.name?.toLowerCase().includes(q) ||
        s.position?.toLowerCase().includes(q) ||
        s.phone?.replace(/\D/g, '').includes(q.replace(/\D/g, ''))
      )
      .slice(0, 4)
      .map(s => ({
        id: 'st-' + s.id,
        type: 'staff',
        icon: null,
        initials: getInitials(s.name),
        color: s.color || '#0ea5e9',
        primary: s.name,
        secondary: s.position,
        action: () => onNavigate('staff'),
      }));

    // Appointments — phone→name fallback dropped. a.clientName is
    // populated by the booking flow in ≥99% of cases; on the rare miss
    // we fall back to the phone number itself. Avoids needing the
    // tenant-wide customers array just to render this one subtitle.
    const aptMatches = (appointments || [])
      .filter(a => {
        const name = a.clientName || '';
        return (
          name.toLowerCase().includes(q) ||
          a.clientPhone?.replace(/\D/g, '').includes(q.replace(/\D/g, '')) ||
          a.date?.includes(q)
        );
      })
      .sort((a, b) => (b.date || '').localeCompare(a.date || ''))
      .slice(0, 5)
      .map(a => {
        const custName = a.clientName || a.clientPhone || '—';
        const svcName  = (a.selectedServices || []).map(s => s.name).join(', ') || a.serviceName || '';
        const dateLabel = fmtDate(a.date) + (a.startTime ? ` · ${a.startTime}` : '');
        const aptDate = a.date ? new Date(a.date + 'T00:00:00') : null;
        return {
          id: 'a-' + a.id,
          type: 'appointment',
          icon: '📅',
          primary: custName,
          secondary: [dateLabel, svcName].filter(Boolean).join(' · '),
          action: () => { if (aptDate) onOpenCalendar(aptDate); onNavigate('calendar'); },
        };
      });

    if (custMatches.length)  items.push({ groupLabel: t('gsearch.customers', 'Клієнти'), items: custMatches });
    if (staffMatches.length) items.push({ groupLabel: t('gsearch.staff',     'Співробітники'), items: staffMatches });
    if (aptMatches.length)   items.push({ groupLabel: t('gsearch.appointments', 'Записи'), items: aptMatches });

    if (!items.length) return [];
    return items;
  }, [query, customerMatches, staff, appointments, quickActions, t, onOpenCustomer, onNavigate, onOpenCalendar]);

  // Flatten for keyboard nav
  const flatItems = useMemo(() => {
    if (!query.trim()) return results; // quick actions are already flat
    return results.flatMap(g => g.items);
  }, [results, query]);

  // Reset selection when results change
  useEffect(() => { setSelected(0); }, [query]);

  // Scroll selected item into view
  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector('.gsearch-item.gsearch-item--active');
    el?.scrollIntoView({ block: 'nearest' });
  }, [selected]);

  const activate = useCallback((item) => {
    item?.action?.();
    onClose();
  }, [onClose]);

  const onKeyDown = useCallback((e) => {
    if (e.key === 'Escape') { onClose(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelected(s => Math.min(s + 1, flatItems.length - 1)); return; }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setSelected(s => Math.max(s - 1, 0)); return; }
    if (e.key === 'Enter')     { e.preventDefault(); activate(flatItems[selected]); return; }
  }, [flatItems, selected, activate, onClose]);

  const isFlat = !query.trim();

  return (
    <div className="gsearch-overlay" onMouseDown={onClose}>
      <div className="gsearch-modal" onMouseDown={e => e.stopPropagation()}>
        {/* Input */}
        <div className="gsearch-input-row">
          <svg className="gsearch-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            ref={inputRef}
            className="gsearch-input"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={t('gsearch.placeholder', 'Пошук клієнтів, записів, співробітників...')}
            autoComplete="off"
          />
          {query && (
            <button className="gsearch-clear" onClick={() => { setQuery(''); inputRef.current?.focus(); }}>✕</button>
          )}
        </div>

        {/* Results */}
        <div className="gsearch-results" ref={listRef}>
          {isFlat ? (
            <>
              <div className="gsearch-group-label">{t('gsearch.quickActions', 'Швидкі дії')}</div>
              {results.map((item, idx) => (
                <button
                  key={item.id}
                  className={`gsearch-item gsearch-item--action${selected === idx ? ' gsearch-item--active' : ''}`}
                  onMouseEnter={() => setSelected(idx)}
                  onClick={() => activate(item)}
                >
                  <span className="gsearch-item-icon">{item.icon}</span>
                  <span className="gsearch-item-primary">{item.label}</span>
                </button>
              ))}
            </>
          ) : results.length === 0 ? (
            <div className="gsearch-empty">
              <span className="gsearch-empty-icon">🔍</span>
              <span>{t('gsearch.noResults', 'Нічого не знайдено')}</span>
            </div>
          ) : (
            (() => {
              let globalIdx = 0;
              return results.map(group => (
                <div key={group.groupLabel}>
                  <div className="gsearch-group-label">{group.groupLabel}</div>
                  {group.items.map(item => {
                    const idx = globalIdx++;
                    return (
                      <button
                        key={item.id}
                        className={`gsearch-item${selected === idx ? ' gsearch-item--active' : ''}`}
                        onMouseEnter={() => setSelected(idx)}
                        onClick={() => activate(item)}
                      >
                        {item.icon ? (
                          <span className="gsearch-item-icon">{item.icon}</span>
                        ) : (
                          <span className="gsearch-avatar" style={{ background: item.color }}>
                            {item.initials}
                          </span>
                        )}
                        <span className="gsearch-item-body">
                          <span className="gsearch-item-primary">{highlight(item.primary, query)}</span>
                          {item.secondary && <span className="gsearch-item-secondary">{highlight(item.secondary, query)}</span>}
                        </span>
                        {item.type === 'customer' && (
                          <span className="gsearch-item-tag">клієнт</span>
                        )}
                        {item.type === 'staff' && (
                          <span className="gsearch-item-tag gsearch-item-tag--staff">майстер</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              ));
            })()
          )}
        </div>

        {/* Footer hints */}
        <div className="gsearch-footer">
          <span><kbd>↑↓</kbd> навігація</span>
          <span><kbd>Enter</kbd> перейти</span>
          <span><kbd>Esc</kbd> закрити</span>
        </div>
      </div>
    </div>
  );
}
